import React, { useEffect, useMemo } from "react";
import { Tab, Nav, Card, Row, Col } from "react-bootstrap";
import {
  Server,
  CalendarEvent,
  FileEarmarkMedical,
  ExclamationCircle,
  Eye,
} from "react-bootstrap-icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ServiceViewRequest from "./ServiceViewRequest";
import ClaimDiagnosisRequest from "./ClaimDiagnosisRequest";
import ClaimVitalSignRequest from "./ClaimVitalSignRequest";
import ClinicalDocumentRequest from "./ClinicalDocumentRequest";
import EncounterDetailsRequest from "./EncounterDetailsRequest";
import ClaimHistoryLogRequest from "./ClaimHistoryLogRequest";
import OpticalDetailsRequest from "./OpticalDetailsequest"; // Note: Fix typo in filename if needed
import ClaimIssueRequest from "./ClaimIssueRequest";
import MedicalInformationRequest from "./MedicalInformationRequest";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { MdHistory } from "react-icons/md";

const PreauthorizationInformations = ({
  control,
  watch,
  setValue,
  activeKey,
  setActiveKey,
  setError,
  clearErrors,
}) => {
  const {
    viewClaimInfo,
    stepFormData,
    preauthorizationResponse,
    claimHistoryLogData,
    validationErrors,
    claimRequestData,
    claimResponseData,
  } = useSelector((state) => state.claim);
  const navigate = useNavigate();

  const { id } = useParams();

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const claimTypeurl = params.get("claim_type");
  const claimRequest = claimRequestData?.entry?.find(
    (item) => item?.resource?.resourceType === "Claim"
  );

  const payloadClaim = viewClaimInfo?.payload_json;

  const isPrescription =
    stepFormData?.is_prescription ||
    payloadClaim?.prescript_submt ||
    claimRequest?.resource?.use === "predetermination";

  const errorRemarkDetails = useMemo(() => {
    return viewClaimInfo?.payer_response?.remarks || [];
  }, [viewClaimInfo]);

  // Parse validation errors from provider_request.valdn_err (same logic as ClaimIssueRequest)
  const providerValidationErrors = useMemo(() => {
    const valdn_err = viewClaimInfo?.provider_request?.valdn_err;
    if (!valdn_err) return [];

    const errors = [];

    // Handle service errors
    if (valdn_err.service && Array.isArray(valdn_err.service)) {
      let currentLineNo = null;
      valdn_err.service.forEach((item) => {
        if (item.no !== undefined) {
          currentLineNo = item.no;
        }
        if (item.msg) {
          errors.push({
            type: "Error",
            message: `In Service No. ${currentLineNo || "Unknown"}: ${item.msg}`,
          });
          currentLineNo = null;
        }
      });
    }

    // Handle diagnosis errors
    if (valdn_err.diag?.msg) {
      errors.push({
        type: "Error",
        message: `Diagnosis: ${valdn_err.diag.msg}`,
      });
    }

    // Handle practitioner errors
    if (valdn_err.pract?.msg) {
      errors.push({
        type: "Error",
        message: `Practitioner: ${valdn_err.pract.msg}`,
      });
    }

    // Handle any other validation errors
    Object.keys(valdn_err).forEach((key) => {
      if (key !== 'service' && key !== 'diag' && key !== 'pract') {
        const error = valdn_err[key];
        if (error?.msg) {
          errors.push({
            type: "Error",
            message: `${key}: ${error.msg}`,
          });
        }
      }
    });

    return errors;
  }, [viewClaimInfo]);

  // Calculate total remarks count
  const totalRemarksCount = useMemo(() => {
    const validationErrorsCount = validationErrors?.length || 0;
    const providerErrorsCount = providerValidationErrors.length;
    const payerRemarksCount = errorRemarkDetails.length;

    return validationErrorsCount + providerErrorsCount + payerRemarksCount;
  }, [validationErrors, providerValidationErrors, errorRemarkDetails]);

  const claimType =
    stepFormData?.claim_type?.claim_types_code?.toLowerCase() ||
    viewClaimInfo?.claim_type?.toLowerCase() ||
    "";
  const isVision = claimType === "vision";
  const isPharmacy = claimType === "pharmacy";

  // Define all possible tabs
  const allTabs = [
    {
      eventKey: "services",
      title: "Services",
      icon: <Server size={16} />,
      errorFields: ["services"],
    },
    {
      eventKey: "encounter",
      title: "Encounter Details",
      icon: <CalendarEvent size={16} />,
      errorFields: ["encounter"],
    },
    {
      eventKey: "optical",
      title: "Optical Details",
      icon: <Eye size={16} />,
      errorFields: ["optical"],
    },
    {
      eventKey: "clinical_doc",
      title: "Documentation",
      icon: <FileEarmarkMedical size={16} />,
      badgeCount: (stepFormData?.attachments?.length || 0) + 2,
      badgeColor: "warning",
      errorFields: ["clinical_doc"],
    },
    {
      eventKey: "claim_issues",
      title: "Remarks",
      icon: <ExclamationCircle size={16} />,
      badgeCount: totalRemarksCount,
      badgeColor: "danger",
      errorFields: ["claim_issues"],
    },
    {
      eventKey: "history",
      title: "History Log",
      icon: <MdHistory size={16} />,
      badgeCount: claimHistoryLogData?.length,
      badgeColor: "primary",
    },
  ];

  // Memoize tabs based on claim type
  const tabs = useMemo(() => {
    let filteredTabs = allTabs;
    if (isVision) {
      filteredTabs = allTabs.filter(
        (tab) => tab.eventKey !== "encounter" && tab.eventKey !== "triage"
      );
    } else if (isPharmacy || isPrescription) {
      filteredTabs = allTabs.filter(
        (tab) => tab.eventKey !== "encounter" && tab.eventKey !== "optical"
      );
    } else if (!isVision) {
      filteredTabs = allTabs.filter((tab) => tab.eventKey !== "optical");
    }

    return filteredTabs;
  }, [
    isVision,
    isPharmacy,
    isPrescription,
    totalRemarksCount,
    stepFormData?.attachments?.length,
    claimHistoryLogData?.length,
  ]);

  // Ensure activeKey is valid when tabs change
  useEffect(() => {
    if (!activeKey || !tabs.some((tab) => tab.eventKey === activeKey)) {
      console.warn("Invalid or undefined activeKey, resetting to 'services'");
      setActiveKey("services");
    }
  }, [activeKey, tabs, setActiveKey]);

  const handleTabChange = (newKey) => {
    const currentTab = tabs.find((tab) => tab.eventKey === activeKey);
    const currentErrorFields = currentTab?.errorFields || [];

    // Check for validation errors in the current tab
    const hasErrors = validationErrors.some((error) =>
      currentErrorFields.includes(error.field)
    );

    if (hasErrors && newKey !== activeKey) {
      toast.warn(
        "Please fix all validation errors before proceeding to the next tab."
      );
      return;
    }

    setActiveKey(newKey);
  };

  return (
    <Card className="border-2  shadow-sm">
      <Card.Body className="p-3">
        <style>{`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.1); }
            100% { transform: scale(1); }
          }
          .pulse-badge {
            animation: pulse 1.5s infinite;
            color: white;
          }
        `}</style>

        <Tab.Container activeKey={activeKey || "services"}>
          <Row>
            <Col xs={12} className="mb-3">
              <Nav
                variant="tabs"
                className="flex-wrap  border-bottom border-danger"
              >
                {tabs.map((tab) => (
                  <Nav.Item key={tab.eventKey}>
                    <Nav.Link
                      eventKey={tab.eventKey}
                      onClick={() => handleTabChange(tab.eventKey)}
                      className={`d-flex align-items-center fs-10 gap-2 ${activeKey === tab.eventKey
                        ? "text-success border-success"
                        : "text-secondary"
                        }`}
                    >
                      {tab.icon}
                      <span className="fw-medium fs-12">
                        {tab.title}
                        {tab.badgeCount > 0 && (
                          <span
                            className={`pulse-badge rounded-circle d-inline-flex align-items-center justify-content-center ms-2 bg-${tab.badgeColor}`}
                            style={{
                              width: "16px",
                              height: "16px",
                              fontSize: "0.5rem",
                              color: "#fff",
                            }}
                          >
                            {tab.badgeCount}
                          </span>
                        )}
                      </span>
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </Col>

            <Col xs={12} className="">
              <Tab.Content>
                <Tab.Pane eventKey="services">
                  <ServiceViewRequest />
                </Tab.Pane>

                <Tab.Pane eventKey="encounter">
                  <EncounterDetailsRequest />
                </Tab.Pane>
                <Tab.Pane eventKey="optical">
                  <OpticalDetailsRequest />
                </Tab.Pane>
                <Tab.Pane eventKey="clinical_doc">
                  <ClinicalDocumentRequest
                    watch={watch}
                    setValue={setValue}
                    control={control}
                    clearErrors={clearErrors}
                    setError={setError}
                  />
                </Tab.Pane>
                <Tab.Pane eventKey="claim_issues">
                  <ClaimIssueRequest />
                </Tab.Pane>
                <Tab.Pane eventKey="history">
                  <ClaimHistoryLogRequest />
                </Tab.Pane>
              </Tab.Content>
            </Col>
          </Row>
        </Tab.Container>
      </Card.Body>
    </Card>
  );
};

export default PreauthorizationInformations;
